import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import { APP_NAME, APP_VIEW_DASHBOARD, APP_VIEW_SETTINGS, APP_VIEW_BACKUP, APP_VIEW_BOARD } from '../constants';

const Sidebar = ({ onNavigate }) => {
  const {
    workspaces, notes, saveNote, activeWorkspaceId, setActiveWorkspaceId, createNote,
    viewMode, setViewMode, showArchived, setShowArchived,
    showArchivedWorkspaces, archiveWorkspace, createWorkspace,
    openConfirm, renameWorkspace, deleteWorkspace, currentUser, logout,
    setIsPaletteOpen
  } = useStore();

  const [isAddingWs, setIsAddingWs] = useState(false);
  const [newWsName, setNewWsName] = useState('');
  const [dropTargetId, setDropTargetId] = useState(null);

  const handleNav = mode => {
    setViewMode(mode);
    onNavigate?.();
  };

  const handleWsSelect = id => {
    setActiveWorkspaceId(id);
    setViewMode(APP_VIEW_BOARD);
    onNavigate?.();
  };

  const handleSearchClick = () => {
    setIsPaletteOpen(true);
    onNavigate?.();
  };

  const handleAddWorkspace = async e => {
    e.preventDefault();
    if (!newWsName.trim()) return;
    await createWorkspace(newWsName.trim());
    setNewWsName('');
    setIsAddingWs(false);
  };

  const handleRename = ws => {
    const name = prompt('Enter new workspace name:', ws.name);
    if (name && name.trim()) renameWorkspace(ws.id, name.trim());
  };

  const handleDrop = async (e, workspaceId) => {
    e.preventDefault();
    const noteId = e.dataTransfer.getData('application/x-stickyhq-note-id') || e.dataTransfer.getData('text/plain');
    const note = notes.find(n => n.id === noteId);
    setDropTargetId(null);
    if (!note || note.workspaceId === workspaceId) return;
    await saveNote({ ...note, workspaceId, inbox: false, movedAt: Date.now() }, true);
  };

  const navItems = [
    { id: APP_VIEW_DASHBOARD, label: 'Dashboard', icon: 'Layout' },
    { id: 'all', label: 'Notes', icon: 'FileText' }
  ];

  return (
    <div className="w-[85vw] max-w-[304px] md:w-64 bg-gray-50 border-r border-gray-200 flex flex-col h-full overflow-hidden shrink-0 safe-top">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center space-x-2 text-gray-800 font-bold text-lg tracking-tight">
          <div className="w-6 h-6 bg-amber-400 rounded flex items-center justify-center"><SafeIcon name="FileText" className="text-gray-900 text-sm" /></div>
          <span>{APP_NAME}</span>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-2">
        <button onClick={() => { createNote(); onNavigate?.(); }} className="w-full bg-white border border-gray-200 hover:border-blue-400 hover:text-blue-600 shadow-sm text-gray-700 py-3 rounded-xl flex items-center justify-center gap-2 transition-all font-bold text-sm">
          <SafeIcon name="Plus" /> New Note
        </button>
        <button onClick={() => { window.dispatchEvent(new CustomEvent('stickyhq:open-quick-capture')); onNavigate?.(); }} className="w-full text-blue-700 bg-blue-50 hover:bg-blue-100 py-2.5 rounded-xl flex items-center justify-center gap-2 transition font-bold text-xs">
          <SafeIcon name="Zap" /> Quick Capture
        </button>
        <button onClick={() => { window.dispatchEvent(new CustomEvent('stickyhq:open-clipboard-history')); onNavigate?.(); }} className="w-full text-gray-700 bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-700 py-2.5 rounded-xl flex items-center justify-center gap-2 transition font-bold text-xs">
          <SafeIcon name="Clipboard" /> Clipboard History
        </button>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="px-2 space-y-1 mb-6">
          <button onClick={handleSearchClick} className="w-full flex md:hidden items-center px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-gray-200"><SafeIcon name="Search" className="mr-3 text-lg" />Search</button>
          {navItems.map(item => (
            <button key={item.id} onClick={() => handleNav(item.id === APP_VIEW_DASHBOARD ? APP_VIEW_DASHBOARD : APP_VIEW_BOARD)} className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-colors ${(item.id === APP_VIEW_DASHBOARD && viewMode === APP_VIEW_DASHBOARD) || (item.id === 'all' && [APP_VIEW_BOARD, 'list', 'timeline'].includes(viewMode)) ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-600 hover:bg-gray-200'}`}>
              <SafeIcon name={item.icon} className="mr-3 text-lg" />{item.label}
            </button>
          ))}
        </div>

        <div className="px-4 mb-2 flex items-center justify-between">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Workspaces</div>
          <button onClick={() => setIsAddingWs(true)} className="p-1.5 hover:bg-gray-200 rounded text-gray-400"><SafeIcon name="Plus" className="text-xs" /></button>
        </div>

        <div className="space-y-1 px-2 mb-6">
          {isAddingWs && (
            <form onSubmit={handleAddWorkspace} className="px-2 mb-2">
              <input autoFocus placeholder="Workspace name..." value={newWsName} onChange={e => setNewWsName(e.target.value)} onBlur={() => !newWsName && setIsAddingWs(false)} className="w-full px-3 py-2 text-sm bg-white border border-blue-300 rounded-lg focus:outline-none" />
            </form>
          )}

          {workspaces.filter(ws => showArchivedWorkspaces ? true : !ws.archivedAt).map(ws => (
            <div key={ws.id} className="group relative">
              <button
                onClick={() => handleWsSelect(ws.id)}
                onDragEnter={e => { e.preventDefault(); setDropTargetId(ws.id); }}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDropTargetId(ws.id); }}
                onDragLeave={() => setDropTargetId(prev => prev === ws.id ? null : prev)}
                onDrop={e => handleDrop(e, ws.id)}
                className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-all ${activeWorkspaceId === ws.id ? 'bg-blue-100 text-blue-800 font-bold' : 'text-gray-600 hover:bg-gray-200'} ${ws.archivedAt ? 'opacity-50 italic' : ''} ${dropTargetId === ws.id ? 'ring-2 ring-blue-400 bg-blue-50 scale-[1.01]' : ''}`}
                title="Drop a note here to move it"
              >
                <div className="w-2.5 h-2.5 rounded-full mr-3" style={{ backgroundColor: ws.color || '#3B82F6' }} />
                <span className="truncate flex-1 text-left">{ws.name}</span>
                {dropTargetId === ws.id && <SafeIcon name="Move" className="text-blue-500 ml-2" />}
              </button>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => handleRename(ws)} className="p-1.5 hover:bg-black/5 rounded text-gray-400 hover:text-blue-500"><SafeIcon name="Edit2" className="text-[10px]" /></button>
                <button onClick={() => openConfirm({ title: ws.archivedAt ? 'Restore Workspace?' : 'Archive Workspace?', message: `Are you sure you want to ${ws.archivedAt ? 'restore' : 'archive'} "${ws.name}"?`, onConfirm: () => archiveWorkspace(ws.id, !!ws.archivedAt) })} className="p-1.5 hover:bg-black/5 rounded text-gray-400 hover:text-orange-500"><SafeIcon name={ws.archivedAt ? 'RotateCcw' : 'Archive'} className="text-[10px]" /></button>
                {ws.id !== 'ws-default' && <button onClick={() => openConfirm({ title: 'Delete Workspace?', message: 'All notes in this workspace will be inaccessible. This cannot be undone.', danger: true, onConfirm: () => deleteWorkspace(ws.id) })} className="p-1.5 hover:bg-red-50 rounded text-gray-300 hover:text-red-500"><SafeIcon name="Trash2" className="text-[10px]" /></button>}
              </div>
            </div>
          ))}
          <div className="px-3 pt-1 text-[10px] text-gray-400 hidden md:block">Tip: drag a note card onto a workspace to move it.</div>
        </div>

        <div className="px-4 mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Filters</div>
        <div className="px-2 space-y-1">
          <button onClick={() => setShowArchived(!showArchived)} className={`w-full flex items-center px-4 py-3 rounded-xl text-sm transition-colors ${showArchived ? 'bg-orange-50 text-orange-700 font-bold' : 'text-gray-600 hover:bg-gray-200'}`}><SafeIcon name="Archive" className="mr-3 text-lg" />{showArchived ? 'Hide Archived' : 'Show Archived'}</button>
          <button onClick={() => { setViewMode(APP_VIEW_DASHBOARD); window.dispatchEvent(new CustomEvent('stickyhq:dashboard-section', { detail: 'favorites' })); onNavigate?.(); }} className="w-full flex items-center px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-gray-200"><SafeIcon name="Heart" className="mr-3 text-lg text-rose-400" />Favorites</button>
          <button onClick={() => { setViewMode(APP_VIEW_DASHBOARD); window.dispatchEvent(new CustomEvent('stickyhq:dashboard-section', { detail: 'recent' })); onNavigate?.(); }} className="w-full flex items-center px-4 py-3 rounded-xl text-sm text-gray-600 hover:bg-gray-200"><SafeIcon name="Clock" className="mr-3 text-lg" />Recently Viewed</button>
        </div>
      </div>

      <div className="p-4 border-t border-gray-200 space-y-1 safe-bottom mb-2">
        <div className="px-3 py-2 flex items-center justify-between mb-2">
          <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">{currentUser?.charAt(0).toUpperCase()}</div><div className="text-sm font-bold text-gray-700 truncate max-w-[100px]">{currentUser}</div></div>
          <button onClick={logout} className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg" title="Logout"><SafeIcon name="LogOut" className="text-sm" /></button>
        </div>
        <button onClick={() => handleNav(APP_VIEW_SETTINGS)} className={`flex items-center text-sm w-full py-2.5 px-3 rounded-xl ${viewMode === APP_VIEW_SETTINGS ? 'bg-gray-200 text-gray-900 font-bold' : 'text-gray-600 hover:bg-gray-200'}`}><SafeIcon name="Settings" className="mr-3 text-lg" /> Settings</button>
        <button onClick={() => handleNav(APP_VIEW_BACKUP)} className={`flex items-center text-sm w-full py-2.5 px-3 rounded-xl ${viewMode === APP_VIEW_BACKUP ? 'bg-gray-200 text-gray-900 font-bold' : 'text-gray-600 hover:bg-gray-200'}`}><SafeIcon name="Database" className="mr-3 text-lg" /> Backup Vault</button>
      </div>
    </div>
  );
};

export default Sidebar;